import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  computeZoomAwareAnchorCenteredPopupLayout,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../../utils/pageZoom';

const HOVER_CLOSE_DELAY_MS = 80;
const POPOVER_GAP_PX = 6;
const VIEWPORT_PAD_PX = 8;
const POPOVER_ESTIMATED_WIDTH_PX = 220;
const POPOVER_ESTIMATED_HEIGHT_PX = 44;

type ProgramImportLearningDisabledHintProps = {
  hint: string | null;
  children: React.ReactElement;
};

/** 비활성「학습내용 가져오기」호버 시 짧은 안내. 네비 ± 줌에 맞춰 위치를 다시 잡는다. */
const ProgramImportLearningDisabledHint: React.FC<ProgramImportLearningDisabledHintProps> = ({
  hint,
  children,
}) => {
  const popoverId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  const updatePopoverPosition = useCallback(() => {
    const wrapEl = wrapRef.current;
    const popoverEl = popoverRef.current;
    if (!wrapEl || !open) return;
    const next = computeZoomAwareAnchorCenteredPopupLayout({
      anchorRect: wrapEl.getBoundingClientRect(),
      popoverWidth: popoverEl?.offsetWidth || POPOVER_ESTIMATED_WIDTH_PX,
      popoverHeight: popoverEl?.offsetHeight || POPOVER_ESTIMATED_HEIGHT_PX,
      gap: POPOVER_GAP_PX,
      pad: VIEWPORT_PAD_PX,
    });
    setPopoverStyle({
      position: 'fixed',
      top: next.top,
      left: next.left,
      zIndex: 10055,
    });
  }, [open]);

  useEffect(() => () => clearCloseTimer(), []);

  useLayoutEffect(() => {
    if (!open) {
      setPopoverStyle(null);
      return;
    }
    updatePopoverPosition();
    const raf = window.requestAnimationFrame(() => updatePopoverPosition());
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, updatePopoverPosition);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, updatePopoverPosition);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!hint) setOpen(false);
  }, [hint]);

  if (!hint) return children;

  const popover =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            className="cp-program-import-learning-disabled-hint-pop"
            role="tooltip"
            style={popoverStyle ?? { position: 'fixed', visibility: 'hidden', zIndex: 10055 }}
          >
            {hint}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <span
        ref={wrapRef}
        className="cp-program-import-learning-disabled-hint"
        onMouseEnter={() => {
          clearCloseTimer();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onFocusCapture={() => {
          clearCloseTimer();
          setOpen(true);
        }}
        onBlurCapture={scheduleClose}
        onClick={() => {
          clearCloseTimer();
          setOpen(true);
        }}
        aria-describedby={open ? popoverId : undefined}
      >
        {children}
      </span>
      {popover}
    </>
  );
};

export default ProgramImportLearningDisabledHint;
